// --------------------------------------------------------------
// Created On: 2024-04-05
// Author: KC Willard
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useMemo, Fragment, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "../../constants/miscellaneous";
import Error500Page from "../Error500Page/Error500Page";
import Spinner from "../../components/Spinner/Spinner";
import useApi from "../../hooks/useApi";
import deepCopy from "../../utilities/deepCopy";
import findExistingCartItem from "../../utilities/findExistingCartItem";
import userHasPermission from "../../utilities/userHasPermission";
import ShoppingCart from "../../components/ShoppingCart/ShoppingCart";
import apiRequest from "../../utilities/api/apiRequest";
import { PLACE_SALES_ORDERS_PERMISSION, PREPARE_QUOTES_PERMISSION } from "../../constants/permissions";
import useRefreshCart from "../../hooks/useRefreshCart";
import useReduxSelector from "src/hooks/useReduxSelector";

// Shopping cart page for current user.
export default function ShoppingCartPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [isQuote, setIsQuote] = useState<boolean>(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [paintColors, setPaintColors] = useState<PaintColor[]>([]);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyDiscount, setCompanyDiscount] = useState<number | null>(null);
  const [distributorAccountCode, setDistributorAccountCode] = useState<string | null>(null);
  const [cartQuantityChangedCounter, setCartQuantityChangedCounter] = useState<number>(0);
  const cartHasError = useMemo(() => cartIsInvalid(cartItems), [JSON.stringify(cartItems)]);
  const navigate = useNavigate();
  const cartQuantity = useReduxSelector((state) => state.cartQuantity);
  const { quoteId } = useParams();

  // Determine whether or not the user is viewing a quote or an order.
  useEffect(() => {
    if (quoteId !== undefined) {
      setIsQuote(true);
    } else {
      setIsQuote(false);
    }
    setLoading(false);
  }, [quoteId]);

  // Refresh the Redux store's cart quantity whenever an item is added to the cart.
  useRefreshCart(() => {
    return cartQuantityChangedCounter > 0;
  }, [cartQuantityChangedCounter]);

  // Shows all shopping cart items for the current user. Allows user to update or remove any existing items.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      method: "GET",
      url: `${API}/cart/map`,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        const cartItems: CartItem[] = [];
        responseBody.items.forEach((cartItem) =>
          cartItems.push({
            cartItemId: cartItem.cartItemId,
            name: cartItem.name,
            quantity: cartItem.quantity,
            quantityIsValid: true,
            units: cartItem.units,
            price: cartItem.price,
            weight: cartItem.weight,
            isPaintable: cartItem.isPaintable,
            paintColorId: cartItem.paintColorId,
            partId: cartItem.partId,
            partNumber: cartItem.partNumber,
            impellerAngle: cartItem.impellerAngle
              ? parseInt(cartItem.impellerAngle).toString()
              : cartItem.impellerAngle,
            impellerAngleIsValid: true,
            impellerTrim: cartItem.impellerTrim
              ? parseFloat(cartItem.impellerTrim).toFixed(2).toString()
              : cartItem.impellerTrim,
            impellerTrimIsValid: true,
            dateAvailable: null,
          })
        );

        setCartItems(cartItems);
        setPaintColors(responseBody.paintColors);
        setCompanyName(responseBody.companyName);
        setCompanyDiscount(responseBody.companyDiscount);
        setDistributorAccountCode(responseBody.distributorAccountCode);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
        console.error("Internal server error. Unable to update shopping cart.");
      }
      setLoading(false);
    },
    [cartQuantity]
  );

  // Make cart put API call.
  async function cartPut(itemsDeepCopy: CartItem[]) {
    setLoading(true);
    const requestBody = {
      items: itemsDeepCopy,
      replaceAll: true,
    };
    const [response] = await apiRequest(`${API}/cart`, "PUT", requestBody);
    if (response.ok) {
      setLoading(false);
    } else {
      setFailedToLoad(true);
      setLoading(false);
      console.error("Internal server error. Unable to update shopping cart.");
    }
  }

  // Function that takes in a single cart item and updates its values if the values are valid.
  function updateCartItem(
    updatedItem: CartItem,
    quantity: string,
    quantityIsValid: boolean,
    paintColorId: number | null,
    impellerTrim: string | null,
    impellerTrimIsValid: boolean,
    impellerAngle: string | null,
    impellerAngleIsValid: boolean
  ) {
    updatedItem.quantityIsValid = quantityIsValid;
    updatedItem.impellerTrimIsValid = impellerTrimIsValid;
    updatedItem.impellerAngleIsValid = impellerAngleIsValid;
    if (quantityIsValid) {
      updatedItem.quantity = quantity;
    }
    if (impellerTrimIsValid && impellerTrim !== null) {
      updatedItem.impellerTrim = parseFloat(impellerTrim).toFixed(2).toString();
    }
    if (impellerAngleIsValid) {
      updatedItem.impellerAngle = impellerAngle;
    }
    updatedItem.paintColorId = paintColorId;
    return updatedItem;
  }

  //Function to combine and remove duplicate items from the cart.
  function combineDuplicateItems(
    existingCartItemId: number,
    updatedItemIndex: number,
    updatedItemQuantity: string,
    cartItems: CartItem[]
  ) {
    const existingItemIndex = cartItems.findIndex((cartItem) => cartItem.cartItemId === existingCartItemId);
    const newQuantity = Number(cartItems[existingItemIndex].quantity) + Number(updatedItemQuantity);
    cartItems[existingItemIndex].quantity = String(newQuantity);
    cartItems.splice(updatedItemIndex, 1);
    return cartItems;
  }

  // Update the cart item. If there is already a cart item with the exact configuration, update the existing cart items quantity.
  async function updateCart(
    itemId: number,
    quantity: string,
    quantityIsValid: boolean,
    paintColorId: number | null,
    impellerTrim: string | null,
    impellerTrimIsValid: boolean,
    impellerAngle: string | null,
    impellerAngleIsValid: boolean
  ) {
    const itemsDeepCopy = deepCopy(cartItems);
    // Get the item we are updating.
    const updatedItem = itemsDeepCopy.find((cartItem) => cartItem.cartItemId === itemId);
    const updatedItemIndex = itemsDeepCopy.findIndex((cartItem) => cartItem.cartItemId === itemId);

    if (updatedItem !== undefined && updatedItemIndex > -1) {
      itemsDeepCopy[updatedItemIndex] = updateCartItem(
        updatedItem,
        quantity,
        quantityIsValid,
        paintColorId,
        impellerTrim,
        impellerTrimIsValid,
        impellerAngle,
        impellerAngleIsValid
      );

      if (quantityIsValid && impellerTrimIsValid && impellerAngleIsValid) {
        // Check to see if the cart item already exists. Update quantity if so.
        const existingCartItemId = findExistingCartItem(itemsDeepCopy[updatedItemIndex], cartItems);
        if (existingCartItemId !== null) {
          combineDuplicateItems(existingCartItemId, updatedItemIndex, quantity, itemsDeepCopy);
        }
      }
      // Set the state of the cart items.
      setCartItems(itemsDeepCopy);
      cartPut(itemsDeepCopy);
      setCartQuantityChangedCounter((previous) => previous + 1);
    }
  }

  // Update the dateAvailable for a given item.
  async function updateCartItemDateAvailable(itemId: number, dateAvailable: string | null) {
    setCartItems((prev) => {
      const itemsDeepCopy = deepCopy(prev);
      // Get the item we are updating.
      const updatedItem = itemsDeepCopy.find((cartItem) => cartItem.cartItemId === itemId);
      const updatedItemIndex = itemsDeepCopy.findIndex((cartItem) => cartItem.cartItemId === itemId);
      if (updatedItem !== undefined && updatedItemIndex > -1) {
        itemsDeepCopy[updatedItemIndex].dateAvailable = dateAvailable;
        return itemsDeepCopy;
      } else {
        return itemsDeepCopy;
      }
    });
  }

  // Delete an item from the cart
  async function deleteCartItem(itemId: number | null) {
    if (itemId === null) {
      return;
    } else {
      const itemsDeepCopy = deepCopy(cartItems);

      // Get the item we are updating.
      const deletedItem = itemsDeepCopy.find((cartItem) => cartItem.cartItemId === itemId);
      const deletedItemIndex = itemsDeepCopy.findIndex((cartItem) => cartItem.cartItemId === itemId);

      if (deletedItem !== undefined && deletedItemIndex > -1) {
        itemsDeepCopy.splice(deletedItemIndex, 1);
        setCartItems(itemsDeepCopy);
        cartPut(itemsDeepCopy);
        setCartQuantityChangedCounter((previous) => previous + 1);
      }
    }
  }

  // Delete all items from the cart.
  async function deleteAllCartItems() {
    setLoading(true);
    const requestBody = {
      items: [],
      replaceAll: true,
    };
    const [response, responseBody] = await apiRequest(`${API}/cart`, "PUT", requestBody);
    if (response.ok && responseBody) {
      setCartItems([]);
      setCartQuantityChangedCounter((previous) => previous + 1);
    } else {
      setFailedToLoad(true);
      console.error("Internal server error. Unable to update shopping cart.");
    }
    setLoading(false);
  }

  // Function to validate all aspects of a cart item are within the acceptable bounds. Set invalid message if they are not.
  function cartIsInvalid(items: CartItem[]) {
    return items.some((item) => {
      if (!item.quantityIsValid || !item.impellerTrimIsValid || !item.impellerAngleIsValid) {
        return true;
      } else {
        return false;
      }
    });
  }

  // Handle updating multiple paint items. If the new quantity is equal to the existing quantity, update the paint color.
  // If the quantity is different, update the existing item with the new paint color and quantity and create a new cart
  // item with the old paint color and remaining quantity.
  async function updateMultiplePaintItems(itemId: number, newPaintColorId: number | null, quantity: string) {
    const itemsDeepCopy = deepCopy(cartItems);
    const updatedItemIndex = itemsDeepCopy.findIndex((item) => item.cartItemId === itemId);
    const updatedItem = itemsDeepCopy[updatedItemIndex];
    if (quantity === updatedItem.quantity) {
      updateCart(
        updatedItem.cartItemId,
        updatedItem.quantity,
        updatedItem.quantityIsValid,
        newPaintColorId,
        updatedItem.impellerTrim,
        updatedItem.impellerTrimIsValid,
        updatedItem.impellerAngle,
        updatedItem.impellerAngleIsValid
      );
    } else {
      const lastCartItemId = itemsDeepCopy[itemsDeepCopy.length - 1].cartItemId;
      const existingPaintColorId = updatedItem.paintColorId;
      const currentQuantity = updatedItem.quantity;
      // Set the new quantity to the existing item.
      itemsDeepCopy[updatedItemIndex].quantity = quantity;
      itemsDeepCopy[updatedItemIndex].paintColorId = newPaintColorId;
      // Create a new cart item and put it at the end of the list of cart items that has the previous quantity minus the
      // quantity selected in the paint modal.
      const newCartItem: CartItem = {
        cartItemId: lastCartItemId + 1,
        paintColorId: existingPaintColorId,
        isPaintable: updatedItem.isPaintable,
        quantity: String(parseInt(currentQuantity) - parseInt(quantity)),
        quantityIsValid: true,
        units: updatedItem.units,
        partId: updatedItem.partId,
        partNumber: updatedItem.partNumber,
        price: updatedItem.price,
        weight: updatedItem.weight,
        impellerAngle: updatedItem.impellerAngle,
        impellerAngleIsValid: true,
        impellerTrim: updatedItem.impellerTrim,
        impellerTrimIsValid: true,
        name: updatedItem.name,
        dateAvailable: null,
      };
      itemsDeepCopy.push(newCartItem);
      setCartItems(itemsDeepCopy);
      cartPut(itemsDeepCopy);
      setCartQuantityChangedCounter((previous) => previous + 1);
    }
  }

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <Fragment>
      <Spinner loading={loading} />
      <div className="container">
        <ShoppingCart
          items={cartItems}
          quoteCartItems={[]}
          previousQuoteCartItems={[]}
          paintColors={paintColors}
          companyName={companyName}
          companyDiscount={companyDiscount}
          accordionView={false}
          distributorAccountCode={distributorAccountCode}
          loading={loading}
          isQuote={isQuote}
          quoteId={quoteId}
          errorMessage=""
          onUpdateMultiplePaintCartItems={(itemId, newPaintColorId, quantity) =>
            updateMultiplePaintItems(itemId, newPaintColorId, quantity)
          }
          onUpdateQuoteCartItemDateAvailable={() => {}}
          onUpdateCartItem={(
            itemId,
            quantity,
            quantityIsValid,
            paintColorId,
            impellerTrim,
            impellerTrimIsValid,
            impellerAngle,
            impellerAngleIsValid
          ) =>
            updateCart(
              itemId,
              quantity,
              quantityIsValid,
              paintColorId,
              impellerTrim,
              impellerTrimIsValid,
              impellerAngle,
              impellerAngleIsValid
            )
          }
          onUpdateCartItemDateAvailable={(itemId, dateAvailable) => updateCartItemDateAvailable(itemId, dateAvailable)}
          onDeleteCartItem={(itemId) => deleteCartItem(itemId)}
          onDeleteAllCartItems={() => deleteAllCartItems()}
        >
          <Fragment>
            {!userHasPermission([[PLACE_SALES_ORDERS_PERMISSION]]) && (
              <p className="text-center">You do not have permission to place sale orders.</p>
            )}
            {cartItems.length > 0 && userHasPermission([[PREPARE_QUOTES_PERMISSION]]) ? (
              <div className="row justify-content-center text-center">
                <div className="col col-lg-5 pb-5 mt-3">
                  <button
                    data-test="checkout-button"
                    className="btn btn-primary w-50"
                    onClick={() => navigate("/submit-order/quote")}
                    disabled={cartHasError}
                  >
                    Prepare Quote
                  </button>
                </div>
                <div className="col col-lg-5 pb-5 mt-3">
                  <button
                    data-test="checkout-button"
                    className="btn btn-primary w-50"
                    onClick={() => navigate("/submit-order")}
                    disabled={cartHasError || !userHasPermission([[PLACE_SALES_ORDERS_PERMISSION]])}
                  >
                    Checkout
                  </button>
                </div>
              </div>
            ) : (
              <div className="row justify-content-center text-center">
                <div className="col col-lg-5 pb-5 mt-3">
                  <button
                    data-test="checkout-button"
                    className="btn btn-primary w-100"
                    onClick={() => navigate("/submit-order")}
                    disabled={cartHasError || !userHasPermission([[PLACE_SALES_ORDERS_PERMISSION]])}
                  >
                    Checkout
                  </button>
                </div>
              </div>
            )}
          </Fragment>
        </ShoppingCart>
      </div>
    </Fragment>
  );
}

interface ResponseBody {
  items: CartItemApiResponse[];
  paintColors: PaintColor[];
  companyName: string | null;
  companyDiscount: number | null;
  distributorAccountCode: string | null;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface ShoppingCart {
  items: CartItem[];
  paintColors: PaintColor[];
  companyName: string;
  companyDiscount: string;
  distributorAccountCode: string;
}

interface CartItemApiResponse {
  cartItemId: number;
  name: string;
  partId: number;
  partNumber: string;
  isPaintable: boolean;
  paintColorId: number | null;
  units: string;
  quantity: string;
  price: number | null;
  weight: number | null;
  impellerTrim: string | null;
  impellerAngle: string | null;
}

interface CartItem {
  cartItemId: number;
  name: string;
  partId: number;
  partNumber: string;
  paintColorId: number | null;
  quantity: string;
  quantityIsValid: boolean;
  units: string;
  price: number | null;
  weight: number | null;
  impellerTrim: string | null;
  impellerTrimIsValid: boolean;
  impellerAngle: string | null;
  impellerAngleIsValid: boolean;
  dateAvailable: string | null;
  isPaintable: boolean;
}
